'use client'

import { useEffect, useRef } from 'react'
import { Stack, Text } from '@mantine/core'
import { SkuPriceOptionsResponse } from '@/types/interfaces/sku'
import { Sku } from '@/types/interfaces/sku'
import { useRouter } from 'next/navigation'
import { useMediaQuery } from '@mantine/hooks'
import { useGetSkuOptionsQuery } from '@/lib/store/api/skuOptionsApi'
import { isInPersonModality } from '@/lib/sku/inPersonVisitRollout'
import { ServiceItemCategory } from '@/types/enums/ServiceItem'
import { trackEvent, SegmentEvents } from '@nile/mantine-theme/analytics'
import {
    type CompactVisitType,
    type VisitType,
    getAvailableVisitTypes,
} from '@/components/VisitOptions/visitOptionsHelpers'
import type { VisitAvailabilityData } from '@/components/VisitOptions/VisitOptionByType'
import { InPersonFirstLayout } from '@/components/VisitOptions/InPersonFirstLayout'
import { DefaultVisitOptionsLayout } from '@/components/VisitOptions/DefaultVisitOptionsLayout'
import { trackVisitOptionsPreviewShown, type VisitOptionsPageLocation, type VisitOptionType } from '@/lib/analytics'
import { useUserContext } from '@/contexts/UserContext'

interface VisitOptionsPreviewProps {
    sku: Sku
    response: SkuPriceOptionsResponse | null
    userHomeTimezone: string | null
    theme?: 'default' | 'tight'
    skuPageVariant?: 'refresh' | 'legacy'
    /**
     * When set, clicks on the in-person visit card will fire
     * `CLICKED_IN_PERSON_VISIT_CARD` with this as the `page_type` metadata.
     * Leave undefined on surfaces that should not contribute to this funnel
     * (e.g. `app/ssr/urgent-care`, `SkuRefreshHeader`).
     *
     * Note: only `'detail'` is valid here. The post-CIQ options page does not
     * render this component — it has its own in-person card and fires the
     * event with `page_type: 'options'` independently.
     */
    pageType?: 'detail'
    /**
     * Whether in-person care is enabled for this SKU + customer. Determined
     * server-side from the feature-flag-aware `getSkuOptions` response and
     * passed in for SSR consistency with the badge / bullets / how-it-works.
     * When omitted (e.g. usages outside the SKU detail page), we fall back
     * to the client-side `useGetSkuOptionsQuery` result.
     */
    inPersonAvailable?: boolean
    /**
     * Page surface this preview is rendered on. Sent on the
     * `visit_options_preview_shown` Segment event so analytics consumers can
     * split SKU detail / SSR landing / SKU refresh header traffic.
     */
    pageLocation: VisitOptionsPageLocation
    showHeader?: boolean
}

export function VisitOptionsPreview({
    sku,
    response,
    userHomeTimezone,
    theme = 'default',
    skuPageVariant,
    inPersonAvailable: inPersonAvailableProp,
    pageLocation,
    pageType,
    showHeader = true,
}: VisitOptionsPreviewProps) {
    const router = useRouter()
    const isMobile = useMediaQuery('(max-width: 48em)') // 48em = 768px
    const { data: skuOptions, isLoading: availabilityLoading } = useGetSkuOptionsQuery(sku.id)
    const { user } = useUserContext()

    const inPersonOption = skuOptions?.options.find((o) => isInPersonModality(o.type))
    // Whether in-person is enabled requires the feature-flag-aware sku options
    // response to include the in-person modality. Prefer the server-passed
    // prop for SSR consistency, falling back to the client query result when
    // not provided.
    const inPersonFlagEnabled = inPersonAvailableProp ?? !!inPersonOption
    const rawAvailableVisitTypes = getAvailableVisitTypes(response)
    const availableVisitTypes = inPersonFlagEnabled
        ? rawAvailableVisitTypes
        : rawAvailableVisitTypes.filter((t) => t !== 'in-person-visit')
    const availabilityData: VisitAvailabilityData = {
        video: skuOptions?.options.find((o) => o.type === 'video')?.availability_promise ?? undefined,
        chat: skuOptions?.options.find((o) => o.type === 'chat')?.availability_promise ?? undefined,
        econsult: skuOptions?.options.find((o) => o.type === 'econsult')?.availability_promise ?? undefined,
    }

    const isTight = theme === 'tight'

    const handleClick = async () => {
        if (!sku || sku.state !== 'enabled') return
        if (skuPageVariant) {
            trackEvent(SegmentEvents.SKU_PAGE_CTA_CLICKED, {
                sku_id: sku.id,
                button_type: 'visit_options',
                variant: skuPageVariant,
            })
        }

        try {
            router.push(`/s/${sku.hyphenated_name}/${sku.id}/screen`)
        } catch (error) {
            console.error('Error handling CTA click:', error)
        }
    }

    // Fire visit_options_preview_shown once availability has resolved.
    // Re-mounts for a new SKU re-emit (correct: the user is seeing a fresh
    // preview). Re-renders that don't change skuId or availability state
    // are debounced via the ref.
    const lastEmittedForSkuRef = useRef<string | null>(null)
    useEffect(() => {
        if (availabilityLoading) return
        const skuKey = `${sku.id}:${pageLocation}`
        if (lastEmittedForSkuRef.current === skuKey) return
        lastEmittedForSkuRef.current = skuKey

        const mappedTypes: VisitOptionType[] = availableVisitTypes.map((t) =>
            isInPersonModality(t) ? 'in_person' : (t as VisitOptionType),
        )

        trackVisitOptionsPreviewShown({
            sku,
            response,
            pageLocation,
            availableVisitTypes: mappedTypes,
            videoAvailability: availabilityData.video,
            chatAvailability: availabilityData.chat,
            isAuthed: !!user,
            isMedicationSku: sku.category === ServiceItemCategory.medication,
        })
    }, [
        sku.id,
        pageLocation,
        availabilityLoading,
        response,
        user,
        sku,
        availabilityData.video,
        availabilityData.chat,
        availableVisitTypes,
    ])

    const handleOfficeVisitClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (pageType) {
            trackEvent(SegmentEvents.CLICKED_IN_PERSON_VISIT_CARD, {
                sku_id: sku.id,
                page_type: pageType,
            })
        }
        handleClick()
    }

    const inPersonAvailable = availableVisitTypes.includes('in-person-visit')
    const secondaryVisitTypes = availableVisitTypes.filter((t): t is CompactVisitType => t !== 'in-person-visit')

    return (
        <Stack gap={24}>
            {showHeader && <Header sku={sku} isTight={isTight} inPersonAvailable={inPersonAvailable} />}
            {inPersonAvailable ? (
                <InPersonFirstLayout
                    sku={sku}
                    prices={response}
                    secondaryVisitTypes={secondaryVisitTypes}
                    onClick={handleClick}
                    onOfficeVisitClick={handleOfficeVisitClick}
                />
            ) : (
                <DefaultVisitOptionsLayout
                    types={availableVisitTypes as VisitType[]}
                    sku={sku}
                    prices={response}
                    userHomeTimezone={userHomeTimezone}
                    availabilityData={availabilityData}
                    availabilityLoading={availabilityLoading}
                    theme={theme}
                    isMobile={!!isMobile}
                    onClick={handleClick}
                    onOfficeVisitClick={handleOfficeVisitClick}
                />
            )}
        </Stack>
    )
}

interface HeaderProps {
    sku: Sku
    isTight: boolean
    inPersonAvailable: boolean
}

function Header({ sku, isTight, inPersonAvailable }: HeaderProps) {
    const isMedicationSku = sku.category === ServiceItemCategory.medication
    const copy = inPersonAvailable
        ? "We'll check any insurance and show you final pricing before you book."
        : isMedicationSku
          ? "Costs shown are for the visit only. You'll pay for medication at your preferred pharmacy."
          : "We'll check any insurance and show you your options before you book."

    return (
        <Stack gap={8}>
            <Text
                fz={24}
                flex={'1 0 0'}
                fw={'var(--gm-weight-display-small, 450)'}
                c={'var(--Foreground-fg-primary, #1F1E1D)'}
                lh={'130%'}
                lts={'-0.24px'}
            >
                Visit options
            </Text>
            <Text
                c={'var(--gm-foreground-fg-secondary, #4F4E4D)'}
                fz={isTight ? 14 : 18}
                style={{ alignItems: 'stretch' }}
                fw={'var(--weight-text, 500)'}
                lh={'130%'}
            >
                {copy}
            </Text>
        </Stack>
    )
}
